import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { Nummernkreis } from "@kundeninfo/api-client";
import { Hinweis } from "@kundeninfo/ui/me-design";
import "../Verwaltung.css";

/**
 * Die Kleinteile, die sich MEHRERE Verwaltungsbereiche teilen (Etappe 2, Aufgabe 2).
 *
 * Sie standen bis hierher am Kopf von views/Verwaltung.tsx — der Datei, in der auch alle
 * zehn Bereiche wohnten. Seit die Bereiche beim Anfassen in eigene Dateien wandern
 * (`views/verwaltung/<bereich>.tsx`), brauchen sie einen Ort, den beide Seiten erreichen:
 * Verwaltung.tsx (für die noch nicht herausgelösten Bereiche) und die herausgelösten
 * Dateien daneben.
 *
 * Die Richtung ist bewusst EINSEITIG: Diese Datei kennt keinen einzigen Bereich und auch
 * nicht `VERWALTUNGS_BEREICHE`. Verwaltung.tsx importiert von hier, nie umgekehrt — sonst
 * entstünde ein Kreis zwischen der Bereichstabelle und ihren Verwendern.
 *
 * **CSS unverändert:** Alles hier zeichnet weiter im Block `vw__` (views/Verwaltung.css).
 * Herausgelöste Bestandsteile werden nicht umbenannt (Hausregel seit Etappe 1, Aufgabe 2);
 * neu ist an dieser Datei nur, WO sie steht.
 */

/**
 * Die zwei Stücke, die ein OBJEKT-Bereich der Verwaltung liefert (Etappe 2, Aufgabe 2).
 *
 * **Beide stehen seit dem Entscheid vom 06.09.2026 im INHALT** — nebeneinander im Rahmen
 * `ObjektBereich` unten. Bis dahin stand die Liste als zweite Ebene in der Verwaltungs-
 * Spalte; sie war dort redundant zum Bereichs-Untermenü und brach in der schmalen Spalte
 * optisch um (Name und Marke einer Zeile schoben sich übereinander). Das Untermenü ist
 * seither wieder das EINZIGE, was in der Spalte steht.
 *
 * Warum trotzdem ein HOOK und keine Komponente — die Frage stellt sich neu, seit der
 * Bereich nur noch EINE Stelle des Bildschirms füllt:
 *
 *  - Sein Zustand muss den BEREICHSWECHSEL überleben. Filter, Suchtext und Blätterung des
 *    Artikelstamms sind die Sicht auf die Liste und keine angefangene Eingabe; wer den
 *    Bereich verlässt und wiederkommt, findet sie wieder vor (festgehalten in
 *    verwaltung.test.tsx). Eine Komponente im Inhalt der Verwaltung würde beim Wechsel
 *    ausgebaut, und mit ihr fiele dieser Zustand.
 *  - Daran hängt der `aktiv`-Effekt, der beim Verlassen genau das Angefangene abräumt
 *    (Dialog, Meldung, Ladefehler) und den Rest stehen lässt. Er wäre ohne Hook ohne
 *    Gegenstand.
 *  - Und die Verdrahtung — Adresse, Navigation, Verlassen-Wächter — gehört ohnehin
 *    App.tsx, wie bei den Bereichen der Etappe 1.
 *
 * Ein Umbau auf Komponenten wäre damit kein Umbenennen, sondern eine Änderung der
 * Lebensdauer — eine eigene Entscheidung, kein Nebenprodukt des Umzugs.
 */
export type VerwaltungBereichTeile = {
  /**
   * Die Objektliste samt Anlege-Knopf und Filtern — die LINKE Spalte des Inhalts.
   *
   * Sie hiess bis zum 06.09.2026 `spalte`, weil sie in der Verwaltungs-Spalte stand. Der
   * neue Name sagt, was sie ist, und nicht mehr, wo sie zufällig hängt.
   */
  liste: ReactElement;
  /** Formular bzw. Detail des gewählten Objekts — die MITTLERE Spalte des Inhalts. */
  detail: ReactElement;
  /**
   * Ein DRITTES, rein lesendes Stück rechts neben dem Detail — heute genau eines: die
   * Positionen einer Angebotsvorlage (Entscheid Dirk 23.09.2026: „Die Positionen wären dann
   * als 4. Spalte praktisch, um einen schnellen Überblick und Einblick zu erhalten." —
   * vierte Spalte, weil das Verwaltungs-Untermenü im Rahmen die erste ist).
   *
   * OPTIONAL, und das ist der Punkt: Die fünf anderen Objektbereiche liefern keins und
   * sehen unverändert aus. Ohne dieses Stück zeichnet der Rahmen die Spalte gar nicht erst
   * — dieselbe Schnittstellenform wie `MeSchaleProps.list`/`context` und aus demselben
   * Grund: Ein Bereich ohne dritte Spalte soll keine bauen müssen, um sie verstecken zu
   * lassen.
   *
   * Sie ist ausdrücklich LESEND. Geändert wird nur im strikten Modal (Hausregel vom
   * 06.09.2026), und das steht bei seinem Knopf im Detail.
   */
  zusatz?: ReactElement | undefined;
};

/**
 * Der Rahmen, der die Stücke eines Objekt-Bereichs nebeneinander stellt.
 *
 * Er steht hier und nicht sechsmal in den Bereichsdateien: Die Aufteilung ist für alle
 * sechs dieselbe, und sie ist eine Aussage über den INHALT der Verwaltung — nicht über den
 * einzelnen Bereich.
 *
 * `objektOffen` ist die Faltung des iPhone-Einspalters (siehe Verwaltung.css): Ist ein
 * Objekt gewählt, steht auf schmalem Gerät NUR es da; die Liste darüber wäre dort eine
 * Bildschirmlänge, durch die man vor jedem Blick aufs Formular scrollen müsste. Breit
 * stehen immer beide nebeneinander. Der Weg zurück auf die Liste ist der Rückweg im Kopf
 * des Rahmens (`zurueckWeg` in App.tsx) — er nennt sie beim Namen.
 *
 * Das dritte Stück (`zusatz`, seit dem 23.09.2026) reiht sich rechts an und bleibt auf
 * schmalem Gerät MIT dem Detail sichtbar — dort steht es unter ihm (Verwaltung.css). Es
 * gehört zum geöffneten Objekt und nicht zur Liste; es mit der Liste einzuklappen nähme
 * genau dem Bildschirm die Auskunft, für den sie gedacht ist.
 */
export function ObjektBereich(
  p: { teile: VerwaltungBereichTeile; objektOffen: boolean },
): ReactElement {
  return (
    <div className={`vw__objektbereich${p.objektOffen ? " vw__objektbereich--offen" : ""}`}>
      <div className="vw__objektliste">{p.teile.liste}</div>
      <div className="vw__objektdetail">{p.teile.detail}</div>
      {/* `undefined` wie „gar nicht": Ein Aufrufer, der die Entscheidung als Ausdruck
          schreibt, soll dafür keine Sonderform brauchen. */}
      {p.teile.zusatz !== undefined && (
        <div className="vw__objektzusatz">{p.teile.zusatz}</div>
      )}
    </div>
  );
}

const KONFLIKT_ZEIT = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
});

/**
 * Dieselbe Formatierung wie in KundeKonditionen.tsx und KundeAnsprechpartner.tsx. Die
 * Doppelung steht hier BENANNT statt versehentlich: Sie zusammenzulegen hiesse, einen
 * gemeinsamen Konfliktkasten zu bauen (nach @kundeninfo/ui gehörend, denn dann sind es
 * drei Aufrufer) — eine eigene Entscheidung samt Umbau der drei Stellen, kein
 * Nebenprodukt dieser Aufgabe.
 */
export function formatKonfliktZeit(iso: string): string {
  const zeitpunkt = new Date(iso);
  if (Number.isNaN(zeitpunkt.getTime())) return iso;
  return `${KONFLIKT_ZEIT.format(zeitpunkt)} Uhr`;
}

/** Die Meldung eines Fehlers — ApiError bringt eine mit, alles andere bekommt die
 *  angegebene Ersatzmeldung. Deckt auch status 0 (NETZWERKFEHLER) mit ab. */
export function meldungAus(fehler: unknown, ersatz: string): string {
  return fehler instanceof ApiError ? fehler.message : ersatz;
}

/**
 * Die Feldfehler einer 422, auf die Felder eingeschränkt, die eine Maske tatsächlich
 * zeigt. Was übrig bleibt, meldet der zweite Rückgabewert — ein Fehler an einem nicht
 * gezeigten Feld darf nicht unsichtbar bleiben (Muster: KundeKonditionen.tsx).
 */
export function feldFehlerAus<F extends string>(
  fehler: ApiError, erlaubt: readonly F[],
): { felder: Partial<Record<F, string>>; unzuordenbar: boolean } {
  const felder: Partial<Record<F, string>> = {};
  let unzuordenbar = false;
  for (const f of fehler.fields ?? []) {
    const treffer = erlaubt.find((e) => e === f.field);
    if (treffer === undefined) { unzuordenbar = true; continue; }
    if (felder[treffer] === undefined) felder[treffer] = f.message;
  }
  return { felder, unzuordenbar };
}

export const KEINE_ZAHL = Symbol("keine Zahl");

/** Text zu Zahl — leer bleibt leer. Keine zweite Fachprüfung (die steht in `domain`),
 *  nur die Umwandlung, und die kann scheitern (Muster: KundeKonditionen.tsx). */
export function alsZahl(eingabe: string): number | null | typeof KEINE_ZAHL {
  const roh = eingabe.trim();
  if (roh === "") return null;
  const zahl = Number(roh.replace(",", "."));
  return Number.isFinite(zahl) ? zahl : KEINE_ZAHL;
}

export function alsFeldwert(zahl: number | null): string {
  return zahl === null ? "" : String(zahl);
}

/**
 * Ein Steuersatz als Prozentzahl — „19,00". Steht hier, weil ihn DREI Bereiche zeigen
 * (Steuersätze, Artikelstamm, Angebotsvorlagen); bis zur Aufgabe 3 wohnten alle drei in
 * einer Datei und teilten sich die Konstante von selbst.
 */
export const SATZ_FORMAT = new Intl.NumberFormat("de-DE",
  { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Die erste Zeile eines Textes als Vorschau in einer Liste — mehr passt nicht in eine
 * Zeile, und der ganze Text steht ohnehin im Editor. Geteilt von Textbausteinen und
 * Angebotsvorlagen (die den Einleitungstext so anreissen).
 */
export function ersteZeile(text: string, hoechstens = 90): string {
  const zeile = (text.split("\n")[0] ?? "").trim();
  return zeile.length <= hoechstens ? zeile : `${zeile.slice(0, hoechstens)}…`;
}

/*
 * Der STRIKTE Modalrahmen stand bis zum 06.09.2026 hier (`Dialog`, `DialogFuss`,
 * `darfAbbrechen`). Er ist nach @kundeninfo/ui gezogen (`Modal`, `ModalFuss`,
 * `darfAbbrechen`, siehe packages/ui/src/overlay/Modal.tsx): Die Hausregel gilt für die
 * ganze Anwendung, nicht nur für die Verwaltung — Kundenakte und Vorgangs-Formulare
 * brauchen denselben Rahmen, und eine zweite Fassung wäre die Stelle, an der ein drittes
 * „Schliessen" oder ein an der Rückfrage vorbeigehendes Escape wieder entstünde.
 *
 * Die Bereichsdateien holen die drei seither unmittelbar aus @kundeninfo/ui; hier steht
 * kein weiterleitender Name mehr, der verspräche, dass die Verwaltung eine eigene Fassung
 * hätte.
 */

/**
 * Ein Ladefehler oder eine Sammelmeldung — seit ME-Etappe 3 (Aufgabe 6) ein `me-hinweis`
 * der Art `fehler` (Rolle `alert` setzt das Element). Der Name bleibt, damit die
 * Bereichsdateien eine Stelle haben, an der „Fehler der Verwaltung" aussieht wie überall.
 */
export function Meldung({ text }: { text: string }): ReactElement {
  return <Hinweis art="fehler">{text}</Hinweis>;
}

/**
 * Eine Eingabe, die die Maske schon VOR dem Aufruf nicht lesen kann (keine Zahl), als
 * dieselbe 422-Gestalt wie ein Befund des Servers — so landet sie über
 * `useFormularZustand` am Feld, und der Fokus springt dorthin (Muster: KundeKonditionen).
 */
export function eingabeUnlesbar(befunde: { field: string; message: string }[]): ApiError {
  return new ApiError(422, "EINGABE_UNLESBAR",
    befunde[0]?.message ?? "Bitte die Eingaben prüfen.", befunde);
}

/**
 * Die fünf Nummernkreise, deutsch beschriftet. Steht hier und nicht mehr beim
 * Nummernkreis-Bereich: Seit dem Paket Unternehmens-Verwaltung braucht der Anlege-Dialog
 * des Unternehmens dieselbe Liste (je Kreis ein Startwert), und zwei Abschriften liefen
 * auseinander.
 */
export const ENTITY_LABEL: Record<Nummernkreis["entity"], string> = {
  CUSTOMER: "Kunden", OFFER: "Angebote", ORDER: "Aufträge", INVOICE: "Rechnungen",
  TICKET: "Tickets",
};

/** Die fünf Kreise in fester Reihenfolge — aus der Beschriftungstabelle abgeleitet, damit
 *  ein fünfter Kreis nicht nur hier oder nur dort auftaucht. */
export const KREISE = Object.keys(ENTITY_LABEL) as Nummernkreis["entity"][];
